import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { useQueryClient } from "react-query";

import useUser from "../../../../hooks/useUser";
import useQuery from "../../../../hooks/useQuery";
import Diagnostic from "./Diagnostic";

const sort = (a, b) => {
  if (!a.id && b.id) return -1;
  if (a.id && !b.id) return 1;
  return b.createdAt - a.createdAt;
};

function Diagnostics({ consumer, uid, className, resource }) {
  const { t } = useTranslation();
  const $container = useRef(null);
  const queryClient = useQueryClient();

  const [status, setStatus] = useState(["chronic", "pending", "intercurrence"]);
  const user = useUser();

  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({});

  // Bug real (auditoría 2026-08-14, mismo patrón ya corregido en Labs.js):
  // `update()` ubicaba las queries de cache con
  // `queryHash.includes("/diagnostics")` — un substring que matchea la de
  // CUALQUIER paciente cacheado (react-query no evict la cache de un
  // paciente sólo porque su componente se desmontó al navegar a otro).
  // Guardar un diagnóstico del paciente ACTUAL terminaba agregándolo
  // también al array cacheado de otro paciente ya visitado en la sesión.
  const endpoint = window._l(`/${consumer.id}/diagnostics`).substr(1);
  const query = useQuery(endpoint, {
    initialData: [],
  });

  const licenses =
    resource &&
    resource.licenses &&
    resource.licenses
      .filter((element) => element.type === "license")
      .map((license) => {
        if (license.jurisdiction === "AR") {
          return `M.N. ${license.value}`;
        } else {
          return `M.P. ${license.value}`;
        }
      })
      .join("-");

  const add = () => {
    const date = new Date();
    const today = `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
    update({
      type: "diagnostics",
      consumer: { id: consumer.id },
      createdBy: user.state.uid,
      createdByName: resource?.label,
      createdByLicense: licenses,
      createdAt: Date.now(),
      status: "chronic",
      startsAt: today,
      endsAt: "",

      cie10Code: "",
      dx: "",
      notes: "",
    });
  };

  const update = (payload, clear) => {
    const id = payload.id || payload.createdAt;
    const keys = queryClient
      .getQueryCache()
      .findAll()
      .filter((query) => query.queryHash.includes(endpoint))
      .map((query) => query.queryKey);

    for (const key of keys) {
      queryClient.setQueryData(key, (records) => {
        const array = JSON.clone(records);
        const index = array.findIndex(
          (record) =>
            (record.id || record.createdAt) === id &&
            record.consumer?.id === payload.consumer?.id
        );
        if (index < 0)
          return [...array, payload].filter((record) => !clear || record.id);
        array[index] = payload;
        return array;
      });
    }
  };

  const toggle = (value) => {
    if (status.includes(value)) {
      setStatus(status.filter((s) => s !== value));
    } else {
      setStatus([...status, value]);
    }
  };

  useEffect(() => {
    const array = [];
    const stats = {};

    for (const record of query.data) {
      if (uid && record.createdBy !== uid) {
        continue;
      }

      if (record.deleted) {
        stats.deleted = stats.deleted || 0;
        stats.deleted += 1;
      }

      if (record.deleted && !status.includes("deleted")) {
        continue;
      }

      if (record.status && !record.deleted) {
        stats[record.status] = stats[record.status] || 0;
        stats[record.status] += 1;
      }

      if (!record.status) {
        record.status = "chronic";
      }

      if (!record.id || (status.includes(record.status) && !record.deleted)) {
        array.push(record);
      } else {
        if (status.includes("deleted") && record.deleted) {
          array.push(record);
        }
      }
    }

    if (query.data.length === 0) {
      add();
    }

    setRecords(array);
    setStats(stats);
  }, [query.data, status, uid]);

  if (!query.isFetched) {
    return (
      <div className={className || ""}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      </div>
    );
  }

  if (query.isLoadingError || query.isError) {
    return (
      <div className={className || ""}>
        <div className="text-danger">{t("error")}</div>
      </div>
    );
  }

  const filters = Object.keys(stats || {}).sort((a, b) => {
    if (a === "deleted" && b !== "deleted") return 1;
    if (a !== "deleted" && b === "deleted") return -1;
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  });

  return (
    <div className={className || ""}>
      <>
        {/* Bug real (auditoría 2026-08-21): sin flex-wrap, con 3+ estados
            activos los pills de filtro se apretaban contra "Nuevo" o
            quedaban fuera de pantalla en mobile. */}
        <div className="d-flex align-items-center flex-wrap">
          <button className="btn btn-sm btn link-success ms-n2" onClick={add}>
            {t("new", { label: t("diagnostic") })}
          </button>

          {filters.length > 0 && (
            <>
              <div className="flex-fill"></div>
              {filters.map((key) => (
                <button
                  key={`status-${key}`}
                  onClick={() => toggle(key)}
                  className={cx("btn btn-sm btn-link", {
                    "link-primary": status.includes(key),
                    "link-secondary": !status.includes(key),
                  })}
                  data-tooltip-id="app-tooltip"
                  data-tooltip-content={t(`${key}_tooltip`)}
                >
                  {t(`ehr:${key}`, { count: stats[key] })}
                  <sup>{stats[key]}</sup>
                </button>
              ))}
            </>
          )}
        </div>
        <div>
          {records.sort(sort).map((record) => (
            <Diagnostic
              key={record.id || record.createdAt}
              record={record}
              update={update}
            />
          ))}
        </div>
      </>
    </div>
  );
}

export default Diagnostics;
